"use client";

import { ArrowLeft, ArrowRight } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Carousel, CarouselApi, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { ExpandableCardPreview } from "@/components/ui/expandable-card-preview";
import { cn } from "@/lib/utils";

function CarouselSkeleton() {
  return (
    <div className="flex gap-6 overflow-hidden px-4 py-2" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="shrink-0 w-[82vw] max-w-[360px] sm:w-[360px] rounded-2xl overflow-hidden border border-black/10 bg-white shadow-sm"
        >
          <div className="skeleton-shimmer h-[240px] w-full" />
          <div className="p-6 flex flex-col gap-3">
            <div className="skeleton-shimmer h-5 w-3/4 rounded-md" />
            <div className="skeleton-shimmer h-3.5 w-full rounded-md" />
            <div className="skeleton-shimmer h-3.5 w-5/6 rounded-md" />
            <div className="mt-4 skeleton-shimmer h-10 w-44 rounded-full" />
          </div>
        </div>
      ))}
    </div>
  );
}

export interface Gallery4Item {
  id: string;
  title: string;
  description: string;
  projectIdxJson: string;
  image: string;
}

export interface Gallery4Props {
  title?: string;
  description?: string;
  items: Gallery4Item[];
  onItemClick?: (item: Gallery4Item) => void;
  showHeader?: boolean;
  autoPlayIntervalMs?: number;
}

const Gallery4 = ({
  title = "Case Studies",
  description = "Explore selected work across our platforms, websites, and digital systems.",
  items,
  onItemClick,
  showHeader = true,
  autoPlayIntervalMs = 1500,
}: Gallery4Props) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const slideCount = useMemo(() => items?.length ?? 0, [items]);

  useEffect(() => {
    if (!carouselApi) return;
    setIsReady(true);
    const updateSelection = () => {
      setCanScrollPrev(carouselApi.canScrollPrev());
      setCanScrollNext(carouselApi.canScrollNext());
      setCurrentSlide(carouselApi.selectedScrollSnap());
    };
    updateSelection();
    carouselApi.on("select", updateSelection);
    return () => {
      carouselApi.off("select", updateSelection);
    };
  }, [carouselApi]);

  // Reinitialize Embla when the window is resized (e.g. crossing the 1024px grid breakpoint)
  // so it remeasures slide widths and the container correctly.
  useEffect(() => {
    if (!carouselApi) return;
    const handleResize = () => {
      carouselApi.reInit();
    };
    window.addEventListener("resize", handleResize, { passive: true });
    return () => window.removeEventListener("resize", handleResize);
  }, [carouselApi]);

  useEffect(() => {
    if (!carouselApi) return;
    if (isPaused) return;
    if (slideCount <= 1) return;
    const id = window.setInterval(() => {
      // scrollNext tends to be more reliable than scrollTo on some mobile browsers.
      carouselApi.scrollNext();
    }, Math.max(800, autoPlayIntervalMs));
    return () => window.clearInterval(id);
  }, [carouselApi, isPaused, autoPlayIntervalMs, slideCount]);

  useEffect(() => {
    // Fallback autoplay: if Embla API never initializes (mobile edge cases),
    // use native scroll snapping on the viewport.
    if (carouselApi) return;
    if (isPaused) return;
    if (slideCount <= 1) return;
    const viewport = rootRef.current?.querySelector<HTMLElement>("[data-carousel-viewport]");
    if (!viewport) return;

    const id = window.setInterval(() => {
      const next = (currentSlide + 1) % slideCount;
      const child = viewport.querySelectorAll<HTMLElement>("[aria-roledescription='slide']")[next];
      if (child) {
        child.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
        setCurrentSlide(next);
      }
    }, Math.max(800, autoPlayIntervalMs));
    return () => window.clearInterval(id);
  }, [carouselApi, isPaused, autoPlayIntervalMs, slideCount, currentSlide]);

  if (!items || items.length === 0) return null;

  return (
    <section className="py-12 md:py-14 lg:py-16">
      {showHeader ? (
        <div className="works-container">
          <div className="mb-8 flex items-end justify-between gap-6 md:mb-14 lg:mb-16">
            <div className="flex flex-col gap-4">
              <h2 className="text-3xl font-medium md:text-4xl lg:text-5xl">{title}</h2>
              <p className="max-w-lg text-muted-foreground">{description}</p>
            </div>
            <div className="hidden shrink-0 gap-2 md:flex">
              <Button
                size="icon"
                variant="ghost"
                onClick={() => carouselApi?.scrollPrev()}
                disabled={!canScrollPrev}
                className="disabled:pointer-events-auto"
              >
                <ArrowLeft className="size-5" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => carouselApi?.scrollNext()}
                disabled={!canScrollNext}
                className="disabled:pointer-events-auto"
              >
                <ArrowRight className="size-5" />
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="w-full" ref={rootRef}>
        {/* Skeleton shown until Embla carousel API initialises */}
        {!isReady && <CarouselSkeleton />}
        <div className={cn("touch-pan-y", !isReady && "sr-only")}>
        <Carousel
          setApi={setCarouselApi}
          opts={{
            loop: true,
            align: "center",
            breakpoints: {
              // dragFree + loop can get stuck on some mobile browsers; keep snaps for reliable autoplay/loop
              "(max-width: 768px)": { dragFree: false, loop: true, align: "center" },
            },
          }}
        >
          <CarouselContent className="px-4 md:px-0 2xl:px-[max(2rem,calc(50vw-700px))]">
            {items.map((item) => (
              <CarouselItem
                key={item.id}
                className="shrink-0 w-[82vw] max-w-[360px] sm:w-[360px]"
              >
                <div className="expandable-card-trigger" data-project-idx={item.projectIdxJson}>
                  <ExpandableCardPreview
                    title={item.title}
                    description={item.description}
                    image={item.image}
                    onClick={() => onItemClick?.(item)}
                  />
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
        </div>

        <div className={cn("mt-4 flex flex-col items-center justify-center gap-2 px-4", !isReady && "invisible")}>
          <p className="text-xs font-medium tracking-wide text-neutral-500">Swipe or tap dots</p>
          <div className="flex flex-wrap justify-center gap-2">
            {items.map((_, index) => (
              <button
                key={index}
                className={cn(
                  "h-2.5 w-2.5 rounded-full transition-colors",
                  currentSlide === index ? "bg-neutral-900" : "bg-neutral-300",
                )}
                onClick={() => {
                  if (carouselApi) {
                    carouselApi.scrollTo(index);
                    return;
                  }
                  const viewport = rootRef.current?.querySelector<HTMLElement>("[data-carousel-viewport]");
                  const child = viewport?.querySelectorAll<HTMLElement>("[aria-roledescription='slide']")[index];
                  child?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
                  setCurrentSlide(index);
                }}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export { Gallery4 };

